import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import routes from '~/routes';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Loading, InlineNotification } from '@carbon/react';
import { createResourceAtom } from 'lib/atoms';
import { formatResource } from 'utils/misc';
import { TypeCurveDetailsContent } from 'pages/type-curves/type-curve-details-content';
import {
  DualSidebarLayout,
  SidebarToggle,
} from 'components/layout/dual-sidebar-layout';
import { EventsPanel } from 'components/events-panel';
import { TypeCurveWorkspaceHeader } from 'components/type-curve-workspace/type-curve-workspace-header';
import { TcPromoteWorkspaceModal } from 'components/type-curve-workspace/tc-promote-workspace-modal';
import { TypeCurvePageFilters } from 'pages/type-curves/type-curve-filters/type-curve-page-filters';
import { TcWellFilterModal } from 'pages/type-curves/type-curve-filters/tc-well-filter-modal';
import { TcUnitFilterModal } from 'pages/type-curves/type-curve-filters/tc-unit-filter-modal';
import { TcColorByModal } from 'pages/type-curves/type-curve-filters/tc-color-by-modal';
import { TcUnitColorByModal } from 'pages/type-curves/type-curve-filters/tc-unit-color-by-modal';
import { TcCrossPlotChartModal } from 'pages/type-curves/type-curve-filters/tc-cross-plot-chart-modal';
import { DcaScenarioSelection } from 'pages/type-curves/dca-scenario-selection';
import { TcVerificationDecisionPanel } from 'components/type-curve-workspace/tc-verification-decision-panel';
import {
  TcVerificationTasksPanel,
  initializeTasksAtom,
  useWorkspaceTasksQuery,
} from 'components/type-curve-workspace/tasks';
import { UnitResolutionPanel } from 'components/type-curve-workspace/unit-resolution/unit-resolution-panel';
import { InventoryGuideModal } from 'components/type-curve-workspace/unit-resolution/inventory-guide-modal';
import { TcAreaSelectorPanel } from 'components/type-curve-workspace/tc-area-selector-panel';
import {
  activeTcWorkspaceAtom,
  latestPromotedWorkspaceIdAtom,
  tcDetailsRightPanelAtom,
  promoteTypeCurveWorkspaceAtom,
  useTcWorkspaceHeaderResourceAtom,
} from 'components/type-curve-workspace/atoms';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import { savedDcaScenarioId, useSeedDcaScenario } from 'lib/dca-scenarios';
import {
  tcDetailsDraftAtom,
  toastAtom,
  inventoryVerificationSummaryAtom,
} from 'pages/type-curves/atoms';
import { tcDcaFitParamsAtom } from 'pages/type-curves/type-curve-dca/dca-fit-atoms';
import {
  tcWellsTotalCountAtom,
  tcUnitsTotalCountAtom,
  tcHasTypeCurveAreaAtom,
  effectivePageSectionVisibilityAtom,
  removedUnitIdsAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import type { TcWorkspaceShowResponse } from 'types/type-curve-workspace-show';
import {
  getTcWorkspaceUnitResolutionVerification,
  getTcWorkspaceInventoryVerification,
  tcWorkspaceSelfHref,
  type TypeCurveWorkspaceResponse,
} from 'types/type-curve-workspace';

import { workspacePromoteBlockers } from './utils';

import style from 'pages/type-curves/type-curves-details.module.scss';

export const TypeCurveWorkspacePage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [leftOpen, setLeftOpen] = useState(true);
  const [editingColorRuleId, setEditingColorRuleId] = useState<string | null>(
    null
  );
  const [editingUnitColorRuleId, setEditingUnitColorRuleId] = useState<
    string | null
  >(null);

  const wellsTotalCount = useAtomValue(tcWellsTotalCountAtom);
  const unitsTotalCount = useAtomValue(tcUnitsTotalCountAtom);
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const sectionVisibility = useAtomValue(effectivePageSectionVisibilityAtom);
  const dcaFitParams = useAtomValue(tcDcaFitParamsAtom);
  const activeTcWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const setActiveTcWorkspace = useSetAtom(activeTcWorkspaceAtom);
  const [rightPanel, setRightPanel] = useAtom(tcDetailsRightPanelAtom);
  const setLatestPromotedWorkspaceId = useSetAtom(
    latestPromotedWorkspaceIdAtom
  );
  const initializeTasks = useSetAtom(initializeTasksAtom);
  const setDraft = useSetAtom(tcDetailsDraftAtom);
  const setHasTypeCurveArea = useSetAtom(tcHasTypeCurveAreaAtom);

  // --- Promote workspace ---
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  // Set as Default travels with the promote and nothing else. Reset whenever the modal opens, so a
  // previous session's tick can never ride along on the next promote unnoticed.
  const [setAsDefault, setSetAsDefault] = useState(false);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [promoteReasons, setPromoteReasons] = useState<string[]>([]);
  const [{ loading: promoteLoading }, promoteWorkspace] = useAtom(
    promoteTypeCurveWorkspaceAtom
  );
  const setToast = useSetAtom(toastAtom);
  const inventoryVerificationSummary = useAtomValue(
    inventoryVerificationSummaryAtom
  );
  const removedUnitIds = useAtomValue(removedUnitIdsAtom);

  // Load verification tasks at this level so the query survives child
  // unmount/remount cycles (e.g. header refetch → Loading fallback).
  useWorkspaceTasksQuery();

  // --- Workspace show fetch ---
  const showHref = id ? `/type_curve_workspaces/${id}` : undefined;
  const showAtom = useMemo(
    () => createResourceAtom<TcWorkspaceShowResponse>(showHref),
    [showHref]
  );
  const [showResource] = useAtom(showAtom);

  // --- Header fetch (parallel) ---
  const headerAtom = useTcWorkspaceHeaderResourceAtom(id);
  const [headerResource, refreshHeader] = useAtom(headerAtom);
  const { data: headerData } = formatResource(headerResource);

  // The page starts on the Type Curve Scenario's Well Control DCA Scenario.
  const typeCurveDcaScenarioId = headerData?.dcaScenario?.id;
  useSeedDcaScenario(typeCurveDcaScenarioId, id);

  const showDcaUnavailableBanner =
    Boolean(headerData) &&
    sectionVisibility.dcaParameters &&
    !headerData?.dcaForecastParameters?.length &&
    Object.keys(dcaFitParams).length === 0;
  // The server's blockers beyond the tasks (an archived Type Curve Area). Selecting
  // an active area refreshes the header, which clears the banner and the block.
  const promoteBlockers = workspacePromoteBlockers(
    activeTcWorkspace,
    headerData
  );
  // Promote the active workspace — mirrors the details page: PATCH the workspace
  // self href, then set the returned (promoted) workspace active and refresh the
  // header so the form re-fetches read-only.
  const handlePromoteConfirm = () => {
    if (!activeTcWorkspace || promoteLoading) return;
    const url = tcWorkspaceSelfHref(activeTcWorkspace);
    if (!url) {
      setPromoteError(
        'Workspace URL is missing from the response. Please refresh and try again.'
      );
      return;
    }
    setPromoteError(null);
    setPromoteReasons([]);
    promoteWorkspace({
      type: 'PATCH',
      url,
      data: { workspace: { promote: true, setAsDefault } },
      onSuccess: (data) => {
        setPromoteModalOpen(false);
        setToast({
          kind: 'success',
          title: 'Verification promoted',
          subtitle: `Persisted to ${activeTcWorkspace.name}.`,
        });

        // Redirect to the type curve details page
        const tcPublicId = (data as unknown as Record<string, unknown>)
          .tcPublicId as string | undefined;
        if (tcPublicId) {
          history.push(`/type_curves/${tcPublicId}`);
        } else {
          // Fallback: stay on page and refresh if tcPublicId is missing
          setActiveTcWorkspace(data);
          void refreshHeader();
        }
      },
      // NOT_PROMOTABLE carries the outstanding items in details.incomplete; keep
      // the generic copy and hang them off it as a tooltip. Any other failure has
      // no reasons to list, so show whatever message the API gave.
      onError: (err?: {
        errors?: {
          code?: string;
          message?: string;
          details?: { incomplete?: string[] };
        }[];
        error?: string;
      }) => {
        const first = err?.errors?.[0];
        const reasons = first?.details?.incomplete ?? [];
        // The server may know a blocker this page loaded before (the area was
        // archived since). Refetch the header so the banner and the disabled
        // Promote catch up.
        if (first?.code === 'NOT_PROMOTABLE') void refreshHeader();
        setPromoteReasons(reasons);
        setPromoteError(
          reasons.length
            ? 'Unable to promote. Please try again.'
            : first?.message ||
                err?.error ||
                'Unable to promote. Please try again.'
        );
      },
    });
  };

  // The right-panel atom is global, so a panel opened on the details page
  // survives the navigation here. 'workspaces' is rendered only by that page, so
  // arriving with it still open left an empty white panel. Close anything this
  // page has no branch for.
  useEffect(() => {
    setRightPanel((prev) => {
      return prev.type === 'workspaces' ? { type: 'dca', open: false } : prev;
    });
    // Mount only — later panel changes are this page's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set activeTcWorkspaceAtom from workspace show data.
  // TcWorkspaceShowResponse is safely castable to TypeCurveWorkspaceResponse
  // because the helper functions in types/type-curve-workspace.ts use
  // Record<string, unknown> internally and gracefully return null for missing
  // verifications/resources.
  useEffect(() => {
    if (
      showResource.data &&
      typeof showResource.data === 'object' &&
      !('error' in showResource.data)
    ) {
      setActiveTcWorkspace(
        showResource.data as unknown as TypeCurveWorkspaceResponse
      );
    }
  }, [showResource.data, setActiveTcWorkspace]);

  // Initialize tasks when workspace is loaded
  useEffect(() => {
    if (!activeTcWorkspace) return;
    const urStatus =
      getTcWorkspaceUnitResolutionVerification(activeTcWorkspace)?.status ??
      null;
    const ivStatus =
      getTcWorkspaceInventoryVerification(activeTcWorkspace)?.status ?? null;
    initializeTasks({
      unitResolutionStatus: urStatus,
      inventoryVerificationStatus: ivStatus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTcWorkspace?.id, initializeTasks]);

  // Initialize the draft from the workspace header for unpromoted workspaces
  useEffect(() => {
    if (!headerData || !activeTcWorkspace || activeTcWorkspace.promoted) {
      setDraft(null);
      return;
    }
    setDraft({
      status: headerData.status ?? '',
      lateralLength:
        headerData.lateralLengthFt !== null &&
        headerData.lateralLengthFt !== undefined
          ? String(headerData.lateralLengthFt)
          : '',
      formation: headerData.formation ?? '',
      wellSpacing:
        headerData.wellSpacingFt !== null &&
        headerData.wellSpacingFt !== undefined
          ? String(headerData.wellSpacingFt)
          : '',
      proppantIntensity:
        headerData.proppantIntensityLbsFt !== null &&
        headerData.proppantIntensityLbsFt !== undefined
          ? String(headerData.proppantIntensityLbsFt)
          : '',
      fluidIntensityGal:
        headerData.fluidIntensityGalFt !== null &&
        headerData.fluidIntensityGalFt !== undefined
          ? String(headerData.fluidIntensityGalFt)
          : '',
      fluidIntensityBbl:
        headerData.fluidIntensityBblFt !== null &&
        headerData.fluidIntensityBblFt !== undefined
          ? String(headerData.fluidIntensityBblFt)
          : '',
      vintage: headerData.vintageDate ?? '',
      operators: headerData.operatorNames ?? [],
      developmentType: headerData.developmentType
        ? [headerData.developmentType]
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTcWorkspace?.id, headerData?.id, setDraft]);

  // Sync latestPromotedWorkspaceId from header data.
  useEffect(() => {
    if (headerData) {
      setLatestPromotedWorkspaceId(
        headerData.latestPromotedWorkspace?.id ?? null
      );
    }
  }, [
    headerData?.latestPromotedWorkspace?.id,
    headerData,
    setLatestPromotedWorkspaceId,
  ]);

  // Empty-state flag: the workspace header carries no Type Curve Area until an
  // area is resolved (e.g. a fresh blank draft). Drives the sidebar disable +
  // data-call suppression. `null` while the header is still loading.
  useEffect(() => {
    setHasTypeCurveArea(
      headerData ? headerData.typeCurveAreaId !== null : null
    );
  }, [headerData, setHasTypeCurveArea]);

  // Clear workspace state on unmount.
  useEffect(() => {
    return () => {
      setActiveTcWorkspace(null);
      setLatestPromotedWorkspaceId(null);
      setHasTypeCurveArea(null);
    };
  }, [setActiveTcWorkspace, setLatestPromotedWorkspaceId, setHasTypeCurveArea]);

  if (!id) {
    return (
      <InlineNotification
        kind="error"
        title="Missing workspace id"
        lowContrast
      />
    );
  }

  if (showResource.loading || headerResource.loading) {
    return <Loading />;
  }

  if (
    showResource.data &&
    typeof showResource.data === 'object' &&
    'error' in showResource.data
  ) {
    return (
      <InlineNotification
        kind="error"
        title={showResource.data.error as string}
        lowContrast
      />
    );
  }

  if (!showResource.data) {
    return (
      <InlineNotification
        kind="error"
        title="Something went wrong"
        lowContrast
      />
    );
  }

  const workspace = showResource.data;

  return (
    <div className={style.pageWrapper}>
      {activeTcWorkspace ? (
        <TypeCurveWorkspaceHeader
          workspace={activeTcWorkspace}
          typeCurveLabel={workspace.uniqueTcId || workspace.name}
          leftPanelOpen={leftOpen}
          onLeftPanelToggle={setLeftOpen}
          onBack={() => {
            const resources = workspace.resources;
            const parentHref =
              resources?.typeCurve?.href ?? resources?.typeCurveHeader?.href;
            history.push(parentHref ?? routes.typeCurvesPath);
          }}
          onPromote={() => {
            setPromoteError(null);
            setPromoteReasons([]);
            setSetAsDefault(false);
            setPromoteModalOpen(true);
          }}
          promoteBlockers={promoteBlockers}
        />
      ) : (
        <div className={style.actionsWrapper}>
          <SidebarToggle
            side="left"
            isOpen={leftOpen}
            onToggle={setLeftOpen}
            label="Panel"
          />
        </div>
      )}

      {showDcaUnavailableBanner && (
        <div className={style.pageBanners}>
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="DCA Parameters are not available for this Type Curve, please check the source."
          />
        </div>
      )}

      <DualSidebarLayout
        left={{
          content: (
            <TypeCurvePageFilters
              wellCount={wellsTotalCount || undefined}
              unitCount={unitsTotalCount || undefined}
              onEditColorRule={setEditingColorRuleId}
              onEditUnitColorRule={setEditingUnitColorRuleId}
              dcaScenarioName={selectedDcaScenario?.name}
              onEditDcaScenario={() =>
                setRightPanel({ type: 'dca', open: true })
              }
              typeCurveAreaName={headerData?.typeCurveAreaName}
            />
          ),
          isOpen: leftOpen,
        }}
        right={{
          content: (
            <>
              {rightPanel.type === 'dca' ? (
                <DcaScenarioSelection
                  onClose={() => setRightPanel({ open: false, type: 'dca' })}
                />
              ) : null}
              {rightPanel.type === 'verification' ? (
                <TcVerificationDecisionPanel kind={rightPanel.kind} />
              ) : null}
              {rightPanel.type === 'tasks' && activeTcWorkspace ? (
                <TcVerificationTasksPanel
                  workspace={activeTcWorkspace}
                  onClose={() => setRightPanel({ open: false, type: 'dca' })}
                  dcaParentId={headerData?.id}
                  dcaScenarioId={savedDcaScenarioId(selectedDcaScenario)}
                  onHeaderChanged={refreshHeader}
                />
              ) : null}
              {rightPanel.type === 'unit-resolution' && activeTcWorkspace ? (
                <UnitResolutionPanel />
              ) : null}
              {rightPanel.type === 'inventory-guide' && activeTcWorkspace ? (
                <InventoryGuideModal />
              ) : null}
              {rightPanel.type === 'tc-area' && activeTcWorkspace ? (
                <TcAreaSelectorPanel
                  workspace={activeTcWorkspace}
                  currentTypeCurveAreaId={headerData?.typeCurveAreaId ?? null}
                  onSaveSuccess={refreshHeader}
                />
              ) : null}
            </>
          ),
          isOpen:
            rightPanel.open &&
            (rightPanel.type !== 'tasks' || Boolean(activeTcWorkspace)),
        }}
      >
        {/* In the main column, so the banner spans the content and not the left panel. */}
        {promoteBlockers.length > 0 && (
          <div className={style.mainBanners}>
            {promoteBlockers.map((blocker) => (
              <InlineNotification
                key={blocker}
                kind="warning"
                lowContrast
                hideCloseButton
                title={blocker}
              />
            ))}
          </div>
        )}
        {headerData ? (
          <TypeCurveDetailsContent
            data={headerData}
            id={id}
            onRefreshHeader={refreshHeader}
          />
        ) : null}
      </DualSidebarLayout>
      {/* The header's comment button only flips isEventsPanelOpenAtom; without a
          panel mounted here it had nothing to open. Hrefs come from the workspace's
          own `resources`, not a hand-built path. */}
      <EventsPanel
        header={workspace.uniqueTcId || workspace.name}
        title="Workspace"
        helperText="Workspace context allows you to share information about this verification."
        eventsHref={workspace.resources?.events?.href}
        commentsHref={workspace.resources?.comments?.href}
      />
      <TcWellFilterModal />
      <TcUnitFilterModal />
      <TcColorByModal
        editingRuleId={editingColorRuleId}
        onClose={() => setEditingColorRuleId(null)}
        typeCurveHeader={headerData ?? null}
      />
      <TcUnitColorByModal
        editingRuleId={editingUnitColorRuleId}
        onClose={() => setEditingUnitColorRuleId(null)}
        typeCurveHeader={headerData ?? null}
      />
      <TcCrossPlotChartModal />
      <TcPromoteWorkspaceModal
        open={promoteModalOpen && Boolean(activeTcWorkspace)}
        workspace={activeTcWorkspace}
        typeCurveLabel={workspace.uniqueTcId || workspace.name}
        loading={promoteLoading}
        error={promoteError}
        errorReasons={promoteReasons}
        inventoryVerificationSummary={inventoryVerificationSummary}
        removedUnitsCount={removedUnitIds.size}
        setAsDefault={setAsDefault}
        onSetAsDefaultChange={setSetAsDefault}
        onClose={() => {
          if (!promoteLoading) {
            setPromoteModalOpen(false);
            setPromoteError(null);
            setPromoteReasons([]);
          }
        }}
        onConfirm={handlePromoteConfirm}
      />
    </div>
  );
};
